import { LocalStorageHandler } from 'utils/Local.storage.handler';
import { Navigation } from 'components/Navigation';
import React from 'react';
import { RouteProps } from 'react-router';
import { Routes } from 'config/routes';
import { useTypedSelector } from 'hooks/useTypedSelector';
import { Route, Redirect } from 'react-router-dom';
import { isExpiredToken } from 'api/karthus.client';
import { useThunkDispatch } from 'hooks';
import { logout } from 'redux/actions/session.actions';
import { resetStore } from '../../redux/actions/types.actions';
import { AccessLevels } from 'config/access.levels';
import { Environment } from 'utils/environment';

type AppRouteProps = {
  accessLevel: number;
} & Omit<RouteProps, 'render'>;

type AppRoutePropsList = {
  accessLevel: number[];
  roles?: string[] | null;
} & Omit<RouteProps, 'render'>;

export const LoginAcces = ({ children, ...props }: RouteProps) => {
  const user = useTypedSelector((s) => s.session.user);

  const render = (): React.ReactNode => {
    if (user.role.id !== 0) {
      return Environment.commissionerActive && user.role.accessLevel === AccessLevels.FinanceManager ? (
        <Redirect to={Routes.comisionist} />
      ) : (
        <Redirect to={Routes.home} />
      );
    } else {
      return children;
    }
  };

  return (
    <>
      <Route {...props}>{render}</Route>
    </>
  );
};

export const AppRoute = ({ accessLevel, children, ...props }: AppRouteProps) => {
  const user = useTypedSelector((s) => s.session.user);
  const token = LocalStorageHandler.getToken();
  const dispatch = useThunkDispatch();

  const isUnauthorized = (): boolean => {
    if (!token) return true;
    if (!user || user.role.accessLevel < accessLevel) return true;
    return false;
  };

  const tokenExpired = isExpiredToken();
  const unauthorized = isUnauthorized();

  React.useEffect(() => {
    if (tokenExpired) {
      dispatch(resetStore());
      dispatch(logout());
      return;
    }

    if (unauthorized) {
      dispatch(resetStore());
    }
  }, [dispatch, tokenExpired, unauthorized]);

  if (tokenExpired || unauthorized) {
    return <Redirect to={Routes.login} />;
  }
  return (
    <>
      <Navigation />
      <Route {...props}>{children}</Route>
    </>
  );
};

export const AppRouteList = ({ accessLevel,roles, children, ...props }: AppRoutePropsList) => {
  const user = useTypedSelector((s) => s.session.user);
  const token = LocalStorageHandler.getToken();
  const dispatch = useThunkDispatch();

  const isUnauthorized = (): boolean => {
    if (!token) return true;
    return false;
  };

  const hasAccess = (): boolean => {

    if(roles){
      return roles.includes(user?.role?.name);
    }
    return accessLevel.includes(user?.role?.accessLevel);
  };

  const tokenExpired = isExpiredToken();
  const unauthorized = isUnauthorized();

  React.useEffect(() => {
    if (tokenExpired) {
      dispatch(resetStore());
      dispatch(logout());
      return;
    }

    if (unauthorized) {
      dispatch(resetStore());
    }
  }, [dispatch, tokenExpired, unauthorized]);

  if (tokenExpired || unauthorized) {
    return <Redirect to={Routes.login} />;
  }

  if (!hasAccess()) {
    return <Redirect to={Routes.home} />;
  }
  return (
    <>
      <Navigation />
      <Route {...props}>{children}</Route>
    </>
  );
};
